import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StatusCuti } from 'src/app/constants/status-cuti';
import { Cuti } from 'src/app/models/cuti.t';
import { DataTableRequest } from 'src/app/models/datatable-request';
import { DialogData } from 'src/app/models/dialog-data';
import { ApplicationService } from 'src/app/services/application.service';
import { CutiService } from 'src/app/services/cuti/cuti.service';
import { TokenStorageService } from 'src/app/services/token-storage/token-storage.service';
import { environment } from 'src/environments/environment';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { PreviewComponent } from '../preview/preview.component';
import { FormCutiComponent } from './form-cuti/form-cuti.component';
import { Saldo } from '../../models/saldo.t';
import { DashboardItem } from '../../models/dashboard-item.t';
import { ApprovalRequest } from '../../models/approval-request.t';
import { FormControl } from '@angular/forms';
import { GeneralOption } from '../../models/general-option.t';
import { DateUtils } from '../../utils/date-utils';
import { InfoCutiComponent } from './info-cuti/info-cuti.component';

@Component({
  selector: 'app-cuti',
  templateUrl: './cuti.component.html',
  styleUrls: ['./cuti.component.css']
})
export class CutiComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: MatPaginator | undefined;
  dataSource: Cuti[] = [];
  displayedColumns: string[] = [
    'nomor',
    'jenis',
    'durasi',
    'tanggal',
    'status',
    'atasan',
    'pejabat',
    'action',
  ];
  page = 0;
  searchTerm = '';
  length = 0;
  pageSize = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  pageEvent: PageEvent | undefined;

  substansi = new FormControl('');
  status = new FormControl('');
  fromDate = new FormControl('');
  toDate = new FormControl('');
  substansies: GeneralOption[] = [];
  statuses: GeneralOption[] = [];

  @ViewChild(MatTable)
  table!: MatTable<Cuti>;
  isLoading = false;
  onDestroy$ = new Subject<void>();

  saldo: DashboardItem[] = [];

  path = '';

  constructor(
    public dialog: MatDialog,
    public tokenStorageService: TokenStorageService,
    private cutiService: CutiService,
    private applicationService: ApplicationService,
    private route: ActivatedRoute,
  ) { }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  ngOnInit(): void {
    this.applicationService.data$.pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        this.substansies = [
          ...data.substansis,
        ];
        this.statuses = [
          ...data.statusCutis,
        ];
      });
    this.route.paramMap.pipe(takeUntil(this.onDestroy$)).subscribe((params) => {
      this.path = params.get('path') || '';
      if (this.isApproval()) {
        this.displayedColumns = [
          'nomor',
          'nama',
          'jenis',
          'durasi',
          'tanggal',
          'status',
          'atasan',
          'pejabat',
          'action',
        ];
      } else {
        this.getSaldo();
      }
      this.reload();
    });

    this.applicationService.unlockScreen$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.reload();
      });

    this.applicationService.reload$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.reload();
      });
  }

  search() {
    this.page = 0;
    this.paginator?.firstPage();
    this.length = 0;
    this.reload();
  }

  reload(event?: PageEvent) {
    const payload: DataTableRequest = {
      page: event ? event.pageIndex : this.page,
      dataPerPage: event ? event.pageSize : this.pageSize,
      searchTerm: this.searchTerm,
      userId: this.tokenStorageService.getId(),
    };
    this.getList(payload);
  }

  openDialog(data?: Cuti) {
    const dialogRef = this.dialog.open(FormCutiComponent, {
      data: data,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.reload();
        if (!this.isApproval()) {
          this.getSaldo();
        }
      }
    });
  }

  openInfoDialog(data?: Cuti) {
    this.dialog.open(InfoCutiComponent, {
      data: data,
      disableClose: true,
    });
  }

  isDraft(cuti: Cuti): boolean {
    return cuti.statusId === StatusCuti.DRAFT;
  }

  isPengajuan(cuti: Cuti): boolean {
    return cuti.statusId === StatusCuti.DIAJUKAN;
  }

  isUserPengajuanMatched(cuti: Cuti) {
    return this.tokenStorageService.isAdminCuti() ||
      this.tokenStorageService.isSuperadmin() ||
      this.tokenStorageService.getId() === cuti.userId
  }

  isApproval() {
    return this.path === 'approval';
  }

  isWaitingAtasan(cuti: Cuti): boolean {
    return cuti.statusId === StatusCuti.DIAJUKAN;
  }

  isWaitingPejabat(cuti: Cuti): boolean {
    return cuti.statusId === StatusCuti.DISETUJUI_ATASAN;
  }

  isUserMatched(cuti: Cuti, isPejabat: boolean) {
    return this.tokenStorageService.isSuperadmin() ||
      (this.tokenStorageService.isAdminCuti() && !isPejabat && this.tokenStorageService.getId() === cuti.atasanId) ||
      (this.tokenStorageService.isAdminCuti() && isPejabat && this.tokenStorageService.getId() === cuti.pejabatId)
  }

  submit(data: Cuti) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: <DialogData>{
        title: 'Peringatan',
        message: 'Apakah Anda yakin akan mengajukan cuti ini?',
        isConfirmation: true,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.isLoading = true;
        this.cutiService.submit(data).subscribe(() => {
          this.isLoading = false;
          this.reload();
        });
      }
    });
  }

  confirmDialog(data: Cuti) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: <DialogData>{
        title: 'Peringatan',
        message: 'Apakah Anda yakin akan menghapus data ini?',
        isConfirmation: true,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.isLoading = true;
        this.cutiService.delete(data).subscribe(() => {
          this.isLoading = false;
          this.reload();
        });
      }
    });
  }

  confirmCancelationDialog(cuti: Cuti) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: <DialogData>{
        title: 'Peringatan',
        message: 'Apakah Anda yakin akan menganulir pengajuan cuti ini?',
        isConfirmation: true,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.isLoading = true;
        const data: ApprovalRequest = {
          id: cuti.id,
          note: result.remark,
          status: StatusCuti.DRAFT,
        };
        this.cutiService.cancel(data).subscribe(() => {
          this.isLoading = false;
          this.reload();
        });
      }
    });
  }

  setPageSizeOptions(setPageSizeOptionsInput: string) {
    if (setPageSizeOptionsInput) {
      this.pageSizeOptions = setPageSizeOptionsInput
        .split(',')
        .map((str) => +str);
    }
  }

  print(cuti: Cuti) {
    this.isLoading = true;
    this.cutiService.print([cuti.id!]).subscribe((data) => {
      const pdf = data.message;
      this.preview(pdf!);
      this.isLoading = false;
    });
  }

  preview(pdf: string) {
    const url =
      environment.apiUrl +
      '/open/download/pdf/cuti?pdfName=' + pdf;
    const dialogRef = this.dialog.open(PreviewComponent, {
      data: {
        viewerUrl: url,
        url: url,
        isPdf: true,
      },
      maxWidth: '90vw',
      maxHeight: '90vh',
      height: '100%',
      width: '100%',
      panelClass: 'full-width-height-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => { });
  }

  preview2(viewerUrl: string, url: string) {
    const dialogRef = this.dialog.open(PreviewComponent, {
      data: {
        viewerUrl: viewerUrl,
        url: url,
        isPdf: true,
      },
      maxWidth: '90vw',
      maxHeight: '90vh',
      height: '100%',
      width: '100%',
      panelClass: 'full-width-height-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => { });
  }

  private getList(payload: DataTableRequest) {
    this.isLoading = true;
    const statusId = this.status.value;
    const dateFrom = DateUtils.format(this.fromDate.value);
    const dateTo = DateUtils.format(this.toDate.value);
    const substansiId = this.substansi.value;

    let request;
    if (this.path === 'approval') {
      request = this.cutiService.getApprovalDatas(payload, substansiId, dateFrom, dateTo, statusId, this.searchTerm);
    } else {
      request = this.cutiService.getDatas(payload);
    }
    request.subscribe((response) => {
      this.table.renderRows();
      this.dataSource = response.datas;
      this.length = response.total;
      this.page = response.page;
      this.isLoading = false;
    });
  }

  private getSaldo() {
    this.cutiService.saldo(this.tokenStorageService.getId()).subscribe((response) => {
      this.saldo = response.data;
    });
  }
}
